import { BodyM, BodyS, DisplayS, HeadingMBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/servicesTabStyle';
import stylesTopUp from 'assets/jss/material-dashboard-pro-react/views/payments/topupStyle';
import { ISelectedPackage } from 'views/PrepaidLoyalty/AddNewServices';
import SingleLine from './subcomponents/SingleLine';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import LimitExceededCard from 'views/TopUpAddOns/commons/LimitExceededCard';
import { MAX_CREDIT_LIMIT } from 'utils/const/services';
import ChangeCreditLimitDialog from 'views/TopUpAddOns/commons/ChangeCreditLimitDialog';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import { PAYMENT_METHODS } from 'views/PrepaidLoyalty/commons/constants';
import classNames from 'classnames';

// @ts-ignore
const useStyles = makeStyles(styles);
// @ts-ignore
const useStylesTopUp = makeStyles(stylesTopUp);

interface ISummaryProps {
	selectedPackages: ISelectedPackage[];
	numberOfLines: number;
	value: number;
	masterNumber: string;
	accountDetails: string;
	accountNumber: string;
	creditLimit: string;
	setCreditLimit: React.Dispatch<React.SetStateAction<string>>;
	userData: CorpUserData | undefined;
	setActiveTab: React.Dispatch<React.SetStateAction<number>>;
	paymentMethod: number;
	groupName?: string;
}

const Summary = ({
	selectedPackages,
	numberOfLines,
	value,
	masterNumber,
	accountDetails,
	accountNumber,
	creditLimit,
	setCreditLimit,
	userData,
	setActiveTab,
	paymentMethod,
	groupName,
}: ISummaryProps) => {
	const classes = useStyles();
	const classesTopUp = useStylesTopUp();
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const vatValue = 0.05;
	const vatPercent = vatValue * 100;
	const priceWithVat = value + value * vatValue;
	const showCreditLimitExceed: boolean =
		paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER && Number(creditLimit) < priceWithVat;
	const showCreditLimitButton: boolean = value <= MAX_CREDIT_LIMIT;
	const showPostpaidNumberButton: boolean = value <= MAX_CREDIT_LIMIT;
	const itemsToChange: types.topUpAddOns.ItemToChange[] = [
		{
			text: translate('prepaid.loyalty.manage.service.details.table.subscriptions.numbers'),
			onClick: () => {
				setActiveTab && setActiveTab(0);
			},
		},
		{
			text: translate('prepaid.loyalty.add.new.services.payment.method.tab'),
			onClick: () => {
				setActiveTab && setActiveTab(1);
			},
		},
		...(showPostpaidNumberButton
			? [
					{
						text: translate('prepaid.loyalty.mobile.postpaid.number'),
						onClick: () => {
							setActiveTab && setActiveTab(1);
						},
					},
			  ]
			: []),
		...(showCreditLimitButton
			? [{ text: translate('numbers.details.credit.limit'), onClick: () => setIsOpen(true) }]
			: []),
	];

	return (
		<div className={classes.servicesTabSummaryContainer}>
			<DisplayS color="primary-light">{translate('summary')}</DisplayS>
			<SingleLine title={translate('prepaid.loyalty.common.nameOfTheGroup')} value={groupName || ''} />
			<SingleLine title={translate('prepaid.loyalty.numbers')} value={numberOfLines} />
			{selectedPackages.map(({ data, type }) => (
				<SingleLine
					key={data.uniqueID}
					title={translate(`prepaid.loyalty.add.new.services.${type}`)}
					subtitle={`${data.shortDescription}
							   ${data.duration ? '/' : ''}
							   ${data.duration ? data.duration : ''}`}
					value={`${translate('price', data.priceOffer)} ${translate('prepaid.loyalty.common.per.line')}`}
				/>
			))}
			<div className={classes.singleLine}>
				<HeadingMBold inline>{translate('payments.topup.totalAmount')}</HeadingMBold>
				<div className={classes.totalAmountWrapper}>
					<div
						className={classNames(classesTopUp.totalAmountContainer, {
							[classesTopUp.dangerColor]: showCreditLimitExceed,
						})}
					>
						<div className={classes.priceWithoutVatWrapper}>
							<DisplayS disableMargin>{translate('price', value.toFixed(3))}</DisplayS>
							<BodyS disableMargin color="disabled-light">
								{translate('prepaid.loyalty.add.new.services.vat.excluded')}
							</BodyS>
						</div>
						<ConditionalRender
							show={paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER}
							onTrue={() => (
								<>
									<div className={classes.divider}></div>
									<div className={classes.creditLimitContainer}>
										<BodyM inline disableMargin color="disabled-light">
											{translate('price', Number(creditLimit).toFixed(3))}
										</BodyM>
										<BodyS disableMargin color="disabled-light">
											{translate('top.up.add.ons.credit.limit')}
										</BodyS>
									</div>
								</>
							)}
						/>
					</div>
					<div id="priceWithVatWrapper">
						{translate('purchase.bulk.devices.price.vat', vatPercent)}
						{translate('price', priceWithVat.toFixed(3))}
					</div>
				</div>
			</div>
			<div>
				<BodyS disableMargin color="disabled-light">
					{translate('prepaid.loyalty.add.new.services.vat.excluded.description')}
				</BodyS>
			</div>
			<ConditionalRender
				show={showCreditLimitExceed}
				onTrue={() => <LimitExceededCard itemsToChange={itemsToChange} />}
			/>
			<ChangeCreditLimitDialog
				isOpen={isOpen}
				hideDialog={() => setIsOpen(false)}
				phoneNumber={masterNumber || ''}
				accountDetails={accountDetails || ''}
				accountNumber={accountNumber || ''}
				creditLimit={creditLimit || ''}
				setCreditLimit={setCreditLimit}
				userData={userData}
			/>
		</div>
	);
};

export default Summary;
